/**
 * The messages of a conversation thread. Demo page: /chats — the person's messages on the right in brand bubbles,
 * everything else on the left, the time under each.
 *
 * A surface can replace a message's body (`renderMessage`, for rows it draws itself) and add a footer under any message
 * (`renderMessageFooter`); both come from `ThreadRenderers`.
 */
import { MessagePrimitive, type TextMessagePartProps, useAuiState } from '@assistant-ui/react';
import type { ReactNode } from 'react';
import { absoluteTime, relativeTime } from '../../utils/relative-time.js';
import { systemLineParts } from './conversation-runtime.js';
import {
  ConversationDataPart,
  MarkdownText,
  PlainText,
  ReasoningLine,
  type ThreadMessageRef,
  ToolCallRow,
  ToolGroup,
  useThreadRenderers,
} from './thread-parts.js';

function useMessageRef(): ThreadMessageRef {
  const id = useAuiState((s) => s.message.id);
  const role = useAuiState((s) => s.message.role);
  const createdAt = useAuiState((s) => s.message.createdAt);
  return { id, role, createdAt };
}

/** The time, then the surface's footer for this message. */
function MessageMeta({ message, align }: { message: ThreadMessageRef; align: 'start' | 'end' }) {
  const running = useAuiState((s) => s.message.status?.type === 'running');
  const footer = useThreadRenderers()?.renderMessageFooter?.(message);
  if (running && !footer) return null;
  const iso = message.createdAt.toISOString();
  return (
    <div className={align === 'end' ? 'flex items-center gap-2 self-end px-1' : 'flex items-center gap-2 px-1'}>
      {!running && (
        <time dateTime={iso} title={absoluteTime(iso)} className="text-xs text-text-tertiary">
          {relativeTime(iso)}
        </time>
      )}
      {footer}
    </div>
  );
}

/** The person's text in the brand bubble. */
function UserText(part: TextMessagePartProps) {
  return (
    <div className="rounded-2xl rounded-br-md bg-chat-list-background-secondary px-4 py-2.5 text-button-primary-text">
      <PlainText {...part} />
    </div>
  );
}

/** The surface's own body for this message, when it draws one. */
function useCustomBody(message: ThreadMessageRef): ReactNode | null {
  return useThreadRenderers()?.renderMessage?.(message) ?? null;
}

/** Who wrote the message, above it, when the surface names authors (a member's name and kind). */
function useMessageHeader(message: ThreadMessageRef): ReactNode | null {
  return useThreadRenderers()?.renderMessageHeader?.(message) ?? null;
}

/** Another person's message (or an external agent's): on the left, under their name, as /chats draws incoming. */
/** A person's attached files, above the message's words: `own` or `others` (`ThreadRenderers.messageAttachments`). */
function MessageFiles({ whose, align }: { whose: 'own' | 'others'; align: 'start' | 'end' }) {
  const Attachment = useThreadRenderers()?.messageAttachments?.[whose];
  if (!Attachment) return null;
  return (
    <div className={align === 'end' ? 'flex flex-wrap justify-end gap-2' : 'flex flex-wrap gap-2'}>
      <MessagePrimitive.Attachments components={{ Attachment }} />
    </div>
  );
}

function IncomingUserMessage({ message }: { message: ThreadMessageRef }) {
  const header = useMessageHeader(message);
  return (
    <MessagePrimitive.Root className="flex max-w-[min(75%,40rem)] flex-col items-start gap-2">
      {header}
      <MessageFiles whose="others" align="start" />
      <div className="rounded-2xl rounded-bl-md bg-chat-list-background-primary-alt px-4 py-2.5 text-text-primary">
        <MessagePrimitive.Parts components={{ Text: PlainText, data: { Fallback: ConversationDataPart } }} />
      </div>
      <MessageMeta message={message} align="start" />
    </MessagePrimitive.Root>
  );
}

export function UserMessage() {
  const message = useMessageRef();
  const custom = useCustomBody(message);
  const incoming = useThreadRenderers()?.isIncoming?.(message) === true;
  if (incoming && !custom) return <IncomingUserMessage message={message} />;
  return (
    <MessagePrimitive.Root className="flex flex-col items-end gap-1">
      {custom ?? (
        <div className="flex max-w-[min(75%,40rem)] flex-col items-end gap-2">
          <MessageFiles whose="own" align="end" />
          <MessagePrimitive.Parts components={{ Text: UserText, data: { Fallback: ConversationDataPart } }} />
        </div>
      )}
      <MessageMeta message={message} align="end" />
    </MessagePrimitive.Root>
  );
}

export function AssistantMessage() {
  const message = useMessageRef();
  const custom = useCustomBody(message);
  const header = useMessageHeader(message);
  return (
    <MessagePrimitive.Root className="flex max-w-[min(85%,48rem)] flex-col items-start gap-2">
      {header}
      {custom ?? (
        <MessagePrimitive.Parts
          components={{
            Text: MarkdownText,
            Reasoning: ReasoningLine,
            tools: { Fallback: ToolCallRow },
            ToolGroup,
            data: { Fallback: ConversationDataPart },
          }}
        />
      )}
      <MessageMeta message={message} align="start" />
    </MessagePrimitive.Root>
  );
}

/** System notices (a member joined, a policy changed): one quiet centred line; cards render in full. */
export function SystemMessage() {
  const message = useMessageRef();
  const custom = useCustomBody(message);
  const renderers = useThreadRenderers();
  const footer = renderers?.renderMessageFooter?.(message);
  const header = renderers?.renderMessageHeader?.(message) ?? null;
  const parts = systemLineParts(useAuiState((s) => s.message.metadata.custom));
  return (
    <MessagePrimitive.Root className="flex flex-col items-center gap-2 self-stretch px-4 text-center text-xs text-text-tertiary">
      {header}
      {custom ??
        (parts ? (
          parts.map((part, index) => {
            // A stored message's parts never reorder, so their position is a stable key.
            const key = `${part.type}-${index}`;
            return part.type === 'text' ? (
              <PlainText key={key} text={part.content} />
            ) : (
              // A card reads left to right; only the line's own text is centred.
              <div key={key} className="w-full min-w-0 text-left">
                {renderers?.renderPart(part, message.id)}
              </div>
            );
          })
        ) : (
          <MessagePrimitive.Parts components={{ Text: PlainText, data: { Fallback: ConversationDataPart } }} />
        ))}
      {footer}
    </MessagePrimitive.Root>
  );
}
